import { NAV_BAR_BORDER_TOP_WIDTH_PX, NAV_BAR_HEIGHT_PX } from '@components/NavBar'
import { usePageTitle } from '@hooks/useTitle'
import { Outlet, useLocation, useResolvedPath } from 'react-router'
import { OrgLevelPermissions, useIsDealerOrg, useOrgInfo } from 'shared-ui/active-operation'
import { BookIcon, Icon } from 'shared-ui/components'
import { SpriteMap, SpriteMap16 } from 'shared-ui/global'
import { useLocalization } from 'shared-ui/localization'
import { isNil, isNonNil } from 'utils'

import { NavigationSidebar } from './SideBarOptions'

const OperationsLayout: React.FC = () => {
  const { pathname } = useLocation()
  const orgInfo = useOrgInfo()
  const isManagerOrSupporter = isNil(orgInfo)
    ? false
    : OrgLevelPermissions.canManage(orgInfo) || OrgLevelPermissions.isSupporter(orgInfo)
  const isDealerOrg = useIsDealerOrg()
  const { t } = useLocalization()

  const links = [
    {
      pathname: useResolvedPath('.').pathname,
      label: t(($) => $.titles.manageOperationOverview),
      icon: <Icon icon={SpriteMap.OverviewIcon} className="text-green" />,
      isVisible: true
    },
    {
      label: t(($) => $.titles.data),
      icon: <Icon icon={SpriteMap.DataManagementIcon} className="text-green" />,
      children: [
        {
          pathname: useResolvedPath('upload-data').pathname,
          label: t(($) => $.titles.applicationDataUpload),
          icon: <Icon icon={SpriteMap16.Upload16} />,
          isVisible: true
        },
        {
          pathname: useResolvedPath('update-fields').pathname,
          label: t(($) => $.buttons.edit),
          icon: <Icon icon={SpriteMap.EditIcon} className="size-4" />,
          isVisible: isManagerOrSupporter
        },
        {
          pathname: useResolvedPath('archive').pathname,
          label: t(($) => $.titles.archive),
          icon: <Icon icon={SpriteMap16.Archive16} />,
          isVisible: isManagerOrSupporter
        },
        {
          pathname: useResolvedPath('history').pathname,
          label: t(($) => $.titles.history),
          icon: <BookIcon color="text-green" size="h-4" />,
          isVisible: false
        }
      ]
    },
    {
      label: t(($) => $.titles.planning),
      icon: <Icon icon={SpriteMap.ChecklistIcon} className="text-green" />,
      isVisible: !isDealerOrg,
      children: [
        {
          pathname: useResolvedPath('fields').pathname,
          label: t(($) => $.agConcepts.field, { count: 2 }),
          icon: <Icon icon={SpriteMap.FieldsIcon} className="size-4" />,
          isVisible: isManagerOrSupporter
        },
        {
          pathname: useResolvedPath('boundaries').pathname,
          label: t(($) => $.titles.boundaries),
          icon: <Icon icon={SpriteMap.FieldBoundariesIcon} className="size-4" />,
          isVisible: isManagerOrSupporter
        },
        {
          pathname: useResolvedPath('prescriptions').pathname,
          label: t(($) => $.titles.prescriptions),
          icon: <Icon icon={SpriteMap.PrescriptionIcon} className="size-4" />,
          isVisible: isManagerOrSupporter
        }
      ]
    },
    {
      pathname: useResolvedPath('software-downloads').pathname,
      label: t(($) => $.titles.softwareUpdates),
      icon: <Icon icon={SpriteMap.DownloadIcon} className="text-green" />,
      isVisible: true
    },
    {
      pathname: useResolvedPath('members').pathname,
      label: t(($) => $.titles.members),
      icon: <Icon icon={SpriteMap.MembersIcon} className="text-green" />,
      isVisible: isManagerOrSupporter
    },
    {
      pathname: useResolvedPath('devices').pathname,
      label: t(($) => $.titles.devices),
      icon: <Icon icon={SpriteMap.Display2020Icon} className="text-green" />,
      isVisible: isManagerOrSupporter && !isDealerOrg
    },
    {
      pathname: useResolvedPath('data-sharing').pathname,
      label: t(($) => $.titles.dataSharing),
      icon: <Icon icon={SpriteMap.DataShareIcon} className="text-green" />,
      isVisible: isManagerOrSupporter
    },
    {
      pathname: useResolvedPath('feedback').pathname,
      label: t(($) => $.titles.feedback),
      icon: <Icon icon={SpriteMap.FeedbackIcon} className="text-green" />,
      isVisible: true
    },

    {
      pathname: useResolvedPath('billing').pathname,
      label: t(($) => $.titles.subscriptions),
      icon: <Icon icon={SpriteMap.CreditCardIcon} className="text-green" />,
      isVisible: isManagerOrSupporter
    }
  ]

  // Recursively get all links (including nested children) for finding activeLabel
  // This includes non-visible links so we can still show the label when navigating directly
  type LinkItem = { pathname?: string; label: string; children?: LinkItem[] }

  const getAllDescendants = (items: LinkItem[]): LinkItem[] =>
    items.flatMap((item) =>
      isNonNil(item.children) ? [item, ...getAllDescendants(item.children)] : [item]
    )

  // Recursively filter to only visible links for the sidebar
  type VisibleLinkItem = {
    pathname?: string
    label: string
    isVisible?: boolean
    children?: VisibleLinkItem[]
  }
  const filterVisibleLinksRecursive = (items: VisibleLinkItem[]): VisibleLinkItem[] => {
    return items.flatMap((item) => {
      if (item.isVisible === false) return []
      if (isNonNil(item.children)) {
        const visibleChildren = filterVisibleLinksRecursive(item.children)
        // Omit children property if there are no visible children
        if (visibleChildren.length > 0) {
          return [{ ...item, children: visibleChildren }]
        }
        // All children hidden — keep as a leaf only if the section has its own pathname,
        // otherwise drop it entirely (a pathnameless leaf would render as NavLink to=""
        // which always matches the current URL and appears permanently active).
        const { children: _children, ...itemWithoutChildren } = item
        return isNonNil(itemWithoutChildren.pathname) ? [itemWithoutChildren] : []
      }
      return [item]
    })
  }

  const visibleLinks = filterVisibleLinksRecursive(links) as typeof links
  const allLinks = getAllDescendants(links)
  const parentLabel = (links as LinkItem[]).find(
    (item) =>
      isNonNil(item.children) &&
      getAllDescendants(item.children).some((l) => l.pathname === pathname)
  )?.label
  const sectionTitle = t(($) =>
    isDealerOrg ? $.manageOperation.dealership : $.manageOperation.operation
  )
  const activeLabel = allLinks.find((l) => l.pathname === pathname)?.label

  usePageTitle(activeLabel ?? sectionTitle)

  return (
    <div
      className="flex"
      style={{
        height: `calc(100vh - ${NAV_BAR_HEIGHT_PX + NAV_BAR_BORDER_TOP_WIDTH_PX}px)`
      }}
    >
      <NavigationSidebar pathname={pathname} sections={visibleLinks} />
      <div className="w-full overflow-y-auto pb-2 pt-6">
        <h6 className="pan-title5 ml-12 flex flex-row items-center gap-2 uppercase text-gray-600 sm:ml-8">
          {sectionTitle}
          {parentLabel !== undefined && (
            <>
              <Icon icon={SpriteMap.ChevronIcon} className="size-3 rotate-90 text-gray-300" />
              {parentLabel}
            </>
          )}
        </h6>
        <div className="w-full min-w-0 px-8 pt-2">
          <Outlet />
        </div>
      </div>
    </div>
  )
}

export default OperationsLayout
